import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';

export type ButtonVariant =
  'primary' | 'ink' | 'secondary' | 'quiet' | 'confirm' | 'ghost' | 'ghostDark' | 'danger';
export type ButtonSize = 'large' | 'medium' | 'small';
export type ButtonShape = 'pill' | 'rounded';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  icon?: IconName;
  iconRight?: IconName;
  fullWidth?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'btnPrimary',
  ink: 'btnInk',
  secondary: 'btnSecondary',
  quiet: 'btnQuiet',
  confirm: 'btnConfirm',
  ghost: 'btnGhost',
  ghostDark: 'btnGhostDark',
  danger: 'btnDanger',
};

const SIZE: Record<ButtonSize, string | null> = {
  large: 'btnLarge',
  medium: null,
  small: 'btnSmall',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'secondary',
    size = 'medium',
    shape = 'pill',
    icon,
    iconRight,
    fullWidth = false,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    'btn',
    VARIANT[variant],
    SIZE[size],
    shape === 'rounded' ? 'btnRounded' : null,
    fullWidth ? 'btnFull' : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {icon ? <Icon name={icon} size={16} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={18} /> : null}
    </button>
  );
});
